<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import type { ExtendedData, Holiday } from '../types/calendar'; // Added Holiday
  import { closeAll, scrollToElement, getElementHeight, isElementVisible } from '../util';
  import { createCalendarStore } from '../stores/calendarStore';
  import { loading, resetLoadingState } from '../stores/user';
  import type { Readable } from 'svelte/store'; // Imported Readable and Writable

  // ------------------- COMPONENT IMPORTS -------------------
  import UserBar from './UserBar.svelte';
  import DayOfTheMonth from './DayOfTheMonth.svelte';
  import CalendarTitle from './CalendarTitle.svelte';
  import SelectMonth from './SelectMonth.svelte';
  import DaysOfTheWeek from './DaysOfTheWeek.svelte';
  import SelectedDay from './SelectedDay.svelte';
  import SharingMessage from './SharingMessage.svelte';
  import ShareConfirm from './ShareConfirm.svelte';
  import ViewItems from './ViewItems.svelte';
  import Loading from './Loading.svelte';

  // ------------------- COMPONENT PROPS -------------------
  interface Props {
    uid: string;
  }

  let { uid }: Props = $props();

  // ------------------- CALENDAR STORE -------------------

  // Define the type for the calendar store
  interface CalendarStoreType {
    currentMonthData: Readable<ExtendedData[]>;
    prevMonthData: Readable<ExtendedData[]>;
    daysInMonth: Readable<ExtendedData[]>;
    startDays: Readable<ExtendedData[]>;
    nextMonthDays: Readable<ExtendedData[]>; // Added for next month's days
    thisMonthsHolidays: Readable<Holiday[]>;
    totalDays: Readable<number>;
    selectedDay: {
      // Not a full Writable, custom object with subscribe and set
      subscribe: (
        this: void,
        run: (value: ExtendedData | undefined) => void,
        invalidate?: (value?: ExtendedData | undefined) => void,
      ) => () => void;
      set: (this: void, value: ExtendedData | undefined) => void;
    };
    setUpdatedValue: (detail: ExtendedData) => void;
    removeItem: (id: string, yearValue: string) => void;
    selectDay: (index: number) => void;
    resetSelectedDay: () => void;
    cleanup: () => void;
    refreshQueries: () => void;
  }

  // Create the calendar store to manage data and state
  let calendarStore = $state<CalendarStoreType | undefined>(undefined);
  let storeInitialized = $state(false);

  // Store values with explicit types
  let currentMonthData = $state<ExtendedData[]>([]);
  let prevMonthData = $state<ExtendedData[]>([]);
  let daysInMonth = $state<ExtendedData[]>([]);
  let startDays = $state<ExtendedData[]>([]);
  let nextMonthDays = $state<ExtendedData[]>([]); // Added state for next month's days
  let totalDays = $state(0);
  let selectedDay = $state<ExtendedData | undefined>(undefined);
  let thisMonthsHolidays = $state<Holiday[]>([]);

  // The old subscribeToStores() function is removed.
  // Its logic is now integrated into initializeCalendarStore.

  // Function to safely subscribe to a store if it exists
  function safeSubscribe<T>(
    store: { subscribe: (callback: (value: T) => void) => () => void } | undefined | null,
    callback: (value: T) => void,
  ): () => void {
    if (!store) {
      return () => {}; // Return a no-op unsubscribe function
    }

    if (typeof store.subscribe !== 'function') {
      return () => {}; // Return a no-op unsubscribe function
    }

    try {
      return store.subscribe((value: T) => {
        try {
          callback(value);
        } catch (error) {
          console.error('Error in store subscription callback:', error);
        }
      });
    } catch (error) {
      console.error('Error subscribing to store:', error);
      return () => {}; // Return a no-op unsubscribe function
    }
  }

  function initializeCalendarStore(userId: string): void {
    try {
      if (calendarStore) {
        try {
          if (typeof calendarStore.cleanup === 'function') {
            calendarStore.cleanup();
          }
        } catch (cleanupError) {
          console.error('Error during calendar store cleanup:', cleanupError);
        }
      }

      if (!userId) {
        storeInitialized = false;
        // Reset loading state if store can't be initialized due to missing userId
        resetLoadingState();
        return;
      }

      calendarStore = createCalendarStore(userId) as CalendarStoreType; // Added type assertion

      if (!calendarStore) {
        console.error('Calendar store creation failed: returned null/undefined');
        storeInitialized = false;
        resetLoadingState();
        return;
      }

      // Validate that the store has the expected methods/properties
      if (!calendarStore.thisMonthsHolidays) {
        console.error('Calendar store missing required properties:', calendarStore);
        storeInitialized = false;
        resetLoadingState();
        return;
      }

      // Manually set up subscriptions to each store
      const unsubscribers: Array<() => void> = [];

      unsubscribers.push(
        safeSubscribe<ExtendedData[]>(calendarStore.currentMonthData, value => {
          currentMonthData = value || [];
        }),
      );

      unsubscribers.push(
        safeSubscribe<ExtendedData[]>(calendarStore.prevMonthData, value => {
          prevMonthData = value || [];
        }),
      );

      unsubscribers.push(
        safeSubscribe<ExtendedData[]>(calendarStore.daysInMonth, value => {
          daysInMonth = value || [];
        }),
      );

      unsubscribers.push(
        safeSubscribe<ExtendedData[]>(calendarStore.startDays, value => {
          startDays = value || [];
        }),
      );

      unsubscribers.push(
        safeSubscribe<ExtendedData[]>(calendarStore.nextMonthDays, value => {
          nextMonthDays = value || [];
        }),
      );

      unsubscribers.push(
        safeSubscribe<number>(calendarStore.totalDays, value => {
          totalDays = value || 0;
        }),
      );

      unsubscribers.push(
        safeSubscribe<ExtendedData | undefined>(calendarStore.selectedDay, value => {
          selectedDay = value;
        }),
      );

      unsubscribers.push(
        safeSubscribe<Holiday[]>(calendarStore.thisMonthsHolidays, value => {
          thisMonthsHolidays = value || [];
        }),
      );

      // Set up unsubscribe function
      unsubscribeFromStores = () => {
        unsubscribers.forEach(unsub => {
          try {
            unsub();
          } catch (e) {
            console.error('Error unsubscribing:', e);
          }
        });
      };

      storeInitialized = true;

      // Reset loading state with a slight delay to allow UI to update
      setTimeout(() => {
        if ($loading) {
          resetLoadingState();
        }
      }, 100);
    } catch (error) {
      console.error('Error initializing calendar store:', error);
      storeInitialized = false;
      // If we can't initialize the store, make sure loading state is false
      resetLoadingState();
    }
  }

  // Store subscription cleanup
  let unsubscribeFromStores: (() => void) | undefined;

  // Initialize store on mount
  onMount(() => {
    if (uid) {
      // Add a short delay to ensure all auth processes have completed
      setTimeout(() => {
        try {
          initializeCalendarStore(uid);
        } catch (err) {
          console.error('Error during calendar store initialization:', err);
          resetLoadingState();
        }
      }, 100);
    } else {
      // Reset loading state if no uid is available
      resetLoadingState();
    }

    // Add a safety timeout to ensure we don't get stuck in loading
    setTimeout(() => {
      if (!storeInitialized && uid) {
        console.warn(
          'Calendar store not initialized after timeout, forcing initialization',
        );
        try {
          initializeCalendarStore(uid);
        } catch (err) {
          console.error('Error during forced calendar store initialization:', err);
          resetLoadingState();
        }
      }

      // Force loading to false if we're still trying to load after timeout
      setTimeout(() => {
        if ($loading) {
          console.warn(
            'Calendar emergency: Forcing loading state to false after safety timeout',
          );
          resetLoadingState();
        }
      }, 1000);
    }, 3000);
  });

  // Watch for uid changes
  $effect(() => {
    if (uid && !storeInitialized) {
      // Add a short delay to ensure reactive properties are updated
      setTimeout(() => {
        initializeCalendarStore(uid);
      }, 100);
    }
  });

  // Loading state management
  $effect(() => {
    // Reset loading state when store is initialized
    if (storeInitialized && $loading) {
      setTimeout(() => {
        resetLoadingState();
      }, 200);
    }
  });

  // Add a function to ensure we don't get stuck in loading state
  function ensureNotStuckInLoading() {
    // Add a safety timeout to ensure we don't get stuck in loading
    setTimeout(() => {
      if (!storeInitialized && uid) {
        console.warn(
          'Calendar store not initialized after timeout, forcing initialization',
        );
        try {
          initializeCalendarStore(uid);
        } catch (error) {
          console.error('Error in safety initialization:', error);
        }
      }

      // Force loading to false after safety timeout regardless of store state
      setTimeout(() => {
        if ($loading) {
          console.warn(
            'Ultimate safety: Forcing loading state to false after extended timeout',
          );
          resetLoadingState();
        }
      }, 1000);
    }, 3000);
  }

  // ------------------- UI STATE MANAGEMENT -------------------
  // UI state elements for responsive design and scrolling behavior
  let mobileInputDiv = $state<HTMLDivElement | undefined>();
  let viewItemsDiv = $state<HTMLDivElement | null>(null);
  let mainOffSetDiv = $state<HTMLElement | null>(null);
  let mainOffSetHeight = $state<number>(0);
  let hasScrolled = $state<boolean>(true);

  // ------------------- EVENT HANDLERS -------------------
  /**
   * Global keydown handler for Escape key
   * Closes all open dropdowns (month selector, year selector, etc.)
   */
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeAll();
    }
  }

  /**
   * Sets the selected day when a calendar cell is clicked
   */
  function selectDay(index: number): void {
    try {
      if (calendarStore && typeof calendarStore.selectDay === 'function') {
        calendarStore.selectDay(index);
      } else if (daysInMonth && daysInMonth.length > index) {
        // Fallback: directly set selected day if calendarStore is not available
        selectedDay = daysInMonth[index];
      }
    } catch (error) {
      console.error('Error in selectDay:', error);
      // Fallback if method throws an error
      if (daysInMonth && daysInMonth.length > index) {
        selectedDay = daysInMonth[index];
      }
    }
  }

  /**
   * Clears the selected day
   */
  function resetSelectedDay(): void {
    try {
      if (calendarStore && typeof calendarStore.resetSelectedDay === 'function') {
        calendarStore.resetSelectedDay();
      } else {
        // Fallback: directly reset selected day if calendarStore is not available
        selectedDay = undefined;
      }
    } catch (error) {
      console.error('Error in resetSelectedDay:', error);
      // Fallback if method throws an error
      selectedDay = undefined;
    }
  }

  // ------------------- UI HELPERS -------------------
  /**
   * Scrolls to the mobile input area when a day is selected
   * Used for better mobile experience
   */
  function scrollToPosition() {
    scrollToElement(mobileInputDiv);
  }

  /**
   * Gets the current height of the main offset div
   * Used for scroll-based UI effects
   */
  function getOffSet() {
    mainOffSetHeight = getElementHeight(mainOffSetDiv);
  }

  /**
   * Tracks scroll position for UI effects
   */
  function onScroll() {
    hasScrolled = isElementVisible(viewItemsDiv, mainOffSetHeight);
  }

  // ------------------- EFFECTS -------------------
  // Update offset height dynamically whenever mainOffSetDiv changes size
  $effect(() => {
    if (mainOffSetDiv) {
      const resizeObserver = new ResizeObserver(() => {
        getOffSet();
      });
      resizeObserver.observe(mainOffSetDiv);
      return () => {
        resizeObserver.disconnect();
      };
    }
  });

  // Cleanup store subscriptions when component is destroyed
  onDestroy(() => {
    // Ensure loading state is reset when component is destroyed
    if ($loading) {
      resetLoadingState();
    }

    if (unsubscribeFromStores) {
      try {
        unsubscribeFromStores();
      } catch (error) {
        console.error('Error during store subscription cleanup:', error);
      }
    }

    if (calendarStore) {
      try {
        if (typeof calendarStore.cleanup === 'function') {
          calendarStore.cleanup();
        }
      } catch (error) {
        console.error('Error during calendar store cleanup:', error);
      }
    }

    // Clear local references
    calendarStore = undefined;
    storeInitialized = false;
  });
</script>

<svelte:window onkeydown={handleKeydown} onresize={getOffSet} onscroll={onScroll} />

{#if !storeInitialized || !calendarStore}
  <div class="loading-container">
    <Loading />
    <p>Initializing calendar...</p>
  </div>
{:else}
  <UserBar reset={resetSelectedDay} />

  <main
    class="calendar"
    class:sixRows={totalDays > 35}
    class:fourRows={totalDays === 28}
    class:selectedDay={selectedDay !== undefined}
    class:hasScrolled
    bind:this={mainOffSetDiv}
  >
    <div class="title-group">
      <CalendarTitle reset={resetSelectedDay} {getOffSet} />
    </div>
    <DaysOfTheWeek />
    {#if startDays && startDays.length > 0}
      {#each startDays as { day, html = '', id = '', event = '', short = '' }}
        <DayOfTheMonth {event} eventShort={short} {html} {id} {day} disabled={true} isPrevMonth={true} />
      {/each}
    {/if}
    {#if daysInMonth && daysInMonth.length > 0}
      {#each daysInMonth as { day, html = '', id = '', event = '', short = '' }, index}
        <DayOfTheMonth
          {event}
          eventShort={short}
          {html}
          {id}
          {day}
          onUpdateValue={(e: { day: number; html: any; id?: string }) =>
            calendarStore && calendarStore.setUpdatedValue(e as ExtendedData)}
          onDeleteValue={(e: { id: string; year: number }) =>
            calendarStore && calendarStore.removeItem(e.id, e.year.toString())}
          onClick={() => selectDay(index)}
        />
      {/each}
    {/if}
    {#if nextMonthDays && nextMonthDays.length > 0}
      {#each nextMonthDays as { day, html = '', id = '', event = '', short = '' }}
        <DayOfTheMonth {event} eventShort={short} {html} {id} {day} disabled={true} isNextMonth={true} />
      {/each}
    {/if}

    {#if selectedDay}
      <SelectedDay
        bind:mobileInput={mobileInputDiv}
        event={selectedDay.event || ''}
        id={selectedDay.id || ''}
        day={selectedDay.day}
        html={selectedDay.html || ''}
        {scrollToPosition}
        onClick={() => calendarStore && calendarStore.resetSelectedDay()}
        onUpdateValue={(e: { day: number; html: any; id?: string }) =>
          calendarStore && calendarStore.setUpdatedValue(e as ExtendedData)}
        onDeleteValue={(e: { id: string; year: number }) =>
          calendarStore && calendarStore.removeItem(e.id, e.year.toString())}
      />
    {/if}
    <SharingMessage location="footer" />
    <SelectMonth reset={resetSelectedDay} />
  </main>

  {#if !selectedDay}
    <div bind:this={viewItemsDiv}>
      <ViewItems data={daysInMonth || []} offSet={mainOffSetHeight} onClick={selectDay} />
    </div>
    <ShareConfirm location="footer" />
  {/if}
{/if}

<style>
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    width: 100%;
  }

  .calendar {
    padding: var(--gutter);
    display: grid;
    gap: 0.5rem;
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: auto auto repeat(5, 1fr) auto;
    height: calc(100vh - 43px);
  }

  .calendar.sixRows {
    grid-template-rows: auto auto repeat(6, 1fr) auto;
  }

  .calendar.fourRows {
    grid-template-rows: auto auto repeat(4, 1fr) auto;
  }

  .calendar .title-group {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  @media screen and (max-height: 1000px) and (min-width: 769px) {
    .calendar .title-group {
      grid-template-columns: auto repeat(5, 1fr) auto;
      gap: 1ch;
    }
  }

  @media screen and (max-width: 768px) {
    .calendar {
      height: unset;
      min-height: 100vh;
      transition:
        box-shadow 300ms,
        background-color 300ms;
    }

    .calendar.hasScrolled {
      box-shadow: 0 0 10px #000000cc;
      background-color: var(--altColor);
    }

    .calendar:not(.selectedDay) {
      grid-template-rows: repeat(7, auto);
      min-height: unset;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 1;
    }

    .calendar.selectedDay {
      grid-template-rows: repeat(7, auto) 250px 40px 1fr;
      min-height: 100vh;
    }

    .calendar.selectedDay.sixRows {
      grid-template-rows: repeat(8, auto) 250px 40px 1fr;
    }

    .calendar.selectedDay.fourRows {
      grid-template-rows: repeat(6, auto) 250px 40px 1fr;
    }
  }

  @media screen and (max-height: 1000px) and (min-width: 769px) {
    .calendar {
      height: 100vh;
      grid-template-rows: auto auto repeat(5, 1fr);
    }

    .calendar.sixRows {
      grid-template-rows: auto auto repeat(6, 1fr);
    }

    .calendar.fourRows {
      grid-template-rows: auto auto repeat(4, 1fr);
    }
  }

  @media print {
    .calendar {
      grid-template-rows: auto auto repeat(5, 1fr);
      gap: 2px;
      height: 100%;
    }

    .calendar.sixRows {
      grid-template-rows: auto auto repeat(6, 1fr);
    }

    .calendar.fourRows {
      grid-template-rows: auto auto repeat(4, 1fr);
    }
  }
</style>
